import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Network, ShieldCheck, Users, Settings, HeadphonesIcon, ArrowRight } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Enterprise — AI Automation at Scale for Large Teams
  Meta Desc: For organizations with 50+ employees. SSO, advanced permissions, custom Streams, dedicated account management, and white-glove onboarding.
  Primary KW: enterprise AI business automation
*/
export function Enterprise() {
  return <main className="pt-20" data-id="element-1538" data-mp="3d1xzg98z3e3p">
      {/* Hero */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-1539" data-mp="3d4twzwgws0rm">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1540" data-mp="3n1ghul98irtn" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1541" data-mp="xb3fhwjih7bh" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-1542" data-mp="hnhyescj5dnd" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1543" data-mp="3os2vh5czm14">
          <span className="eyebrow" data-id="element-1544" data-mp="3r4ivork3mbj">Enterprise</span>
          <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6" data-id="element-1545" data-mp="3uw4ktye2g3i3">
            MISTR. <span className="text-gradient" data-id="element-1546" data-mp="3q2tdzx997mq6">At Scale.</span>
          </h1>
          <p className="text-lg md:text-xl text-steel mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-1547" data-mp="3l4i63s2irmal">
            For organizations with 50+ employees. SSO, advanced permissions,
            custom Streams, dedicated account management, and white-glove
            onboarding.
          </p>
          <button className="btn-primary" data-id="element-1548" data-mp="374ywsvxz24wp">Talk to Sales →</button>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1549" data-mp="2xh3d4t6hbutn" />

      {/* Who it's for */}
      <section className="section-padding bg-dark-bg relative" data-id="element-1550" data-mp="32ialatcjaw0u">
        <div className="inner-container relative z-10" data-id="element-1551" data-mp="2nki9oca3yrrx">
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1552" data-mp="2nm33u0sig8p9">
            {[{
            icon: <Building2 size={32} data-id="element-1553" data-mp="2skmq62b0nmi5" />,
            title: 'Large Businesses',
            desc: 'Standardize operations across departments. Ensure every team follows the same automated processes.',
            color: 'text-teal'
          }, {
            icon: <Network size={32} data-id="element-1554" data-mp="36ib04f3jp98f" />,
            title: 'Agencies',
            desc: 'Manage multiple client accounts from a single master dashboard with strict data separation.',
            color: 'text-purple'
          }, {
            icon: <Users size={32} data-id="element-1555" data-mp="7tm7xqks4uyv" />,
            title: 'Franchises',
            desc: 'Deploy identical operational Streams to every new location instantly. Maintain brand consistency automatically.',
            color: 'text-red-cta'
          }].map((audience, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all group" data-id="element-1556" data-mp="227rfkux6jab9" data-mpi={i}>
                <div className={`${audience.color} mb-6 bg-white/5 w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-1557" data-mp="2pydnmm76oul9">
                  {audience.icon}
                </div>
                <h3 className="font-poppins font-bold text-xl mb-4" data-id="element-1558" data-mp="3panjxnmyi9cn">
                  {audience.title}
                </h3>
                <p className="text-steel" data-id="element-1559" data-mp="3ubb6nx45k412">{audience.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1560" data-mp="81ifff9bxrsx" />

      {/* Enterprise Features */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1561" data-mp="c193vcbdvvua">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1562" data-mp="ltf3973ytfji" />

        <div className="inner-container relative z-10" data-id="element-1563" data-mp="qqezyrexd5xy">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1564" data-mp="vsnvnvj6mm3r">
            <span className="eyebrow" data-id="element-1565" data-mp="2g9nbfpf596ev">Enterprise Capabilities</span>
            <h2 className="h2-title" data-id="element-1566" data-mp="2gayfzrgeyi71">Built for Complex Organizations</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1567" data-mp="2b8zb2d50zd7z">
            {[{
            icon: <ShieldCheck size={24} data-id="element-1568" data-mp="26bpn71xab9m9" />,
            title: 'SSO & SAML',
            desc: 'Integrate with Okta, Azure AD, or Google Workspace for secure, centralized access control.'
          }, {
            icon: <Settings size={24} data-id="element-1569" data-mp="2u1bmdpdnpou9" />,
            title: 'Advanced Permissions',
            desc: 'Granular role-based access control. Restrict who can view, edit, or deploy specific Streams.'
          }, {
            icon: <Network size={24} data-id="element-1570" data-mp="2kagzcisl6l7r" />,
            title: 'Custom Stream Builds',
            desc: 'Our engineering team builds complex, multi-step integrations specific to your proprietary systems.'
          }, {
            icon: <Users size={24} data-id="element-1571" data-mp="1b2dnax0iid0p" />,
            title: 'Dedicated Account Manager',
            desc: 'A named success manager who understands your business and meets with you quarterly.'
          }, {
            icon: <ShieldCheck size={24} data-id="element-1572" data-mp="11bj27vz05jgu" />,
            title: 'SLA Guarantee',
            desc: '99.9% uptime guarantee with financially backed service level agreements.'
          }, {
            icon: <HeadphonesIcon size={24} data-id="element-1573" data-mp="sab4bf0eekna" />,
            title: 'White-Glove Onboarding',
            desc: 'We handle the entire migration and setup process, including team training sessions.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-teal/40 hover:shadow-[0_0_20px_rgba(7,133,183,0.15)] transition-all group" data-id="element-1574" data-mp="ncjaugk3uqgz" data-mpi={i}>
                <div className="text-teal mb-4 bg-teal/10 w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1575" data-mp="b18u9ftafr8r">
                  {feature.icon}
                </div>
                <h3 className="font-poppins font-bold text-lg mb-2 group-hover:text-teal transition-colors" data-id="element-1576" data-mp="jwya79byvter">
                  {feature.title}
                </h3>
                <p className="text-steel text-sm" data-id="element-1577" data-mp="tucrtc9mmo8n">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1578" data-mp="2vl6dmha7k1p3" />

      {/* Onboarding Timeline */}
      <section className="section-padding bg-dark-bg relative" data-id="element-1579" data-mp="29qpljvv3eht9">
        <div className="inner-container max-w-5xl mx-auto relative z-10" data-id="element-1580" data-mp="2epsckce8mni8">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1581" data-mp="2jh22le3p7sun">
            <span className="eyebrow" data-id="element-1582" data-mp="2jhk5gtkv95bj">Implementation</span>
            <h2 className="h2-title" data-id="element-1583" data-mp="2ogn51yi2ujc4">How Enterprise Onboarding Works</h2>
          </div>

          <div className="relative" data-id="element-1584" data-mp="1qsmsn5zfc0q6">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-white/10 -translate-y-1/2 z-0" data-id="element-1585" data-mp="1quprmfik1471"></div>

            <div className="grid md:grid-cols-4 gap-8 relative z-10" data-id="element-1586" data-mp="20qstrldep2tz">
              {[{
              step: '01',
              title: 'Discovery',
              desc: 'We audit your current tech stack and identify the highest-ROI automation opportunities.'
            }, {
              step: '02',
              title: 'Stream Mapping',
              desc: 'Our engineers design custom Streams tailored to your specific operational workflows.'
            }, {
              step: '03',
              title: 'Guided Rollout',
              desc: 'Phased deployment with hands-on training for your department heads and team.'
            }, {
              step: '04',
              title: 'Optimization',
              desc: 'Ongoing monitoring and quarterly business reviews to ensure maximum efficiency.'
            }].map((phase, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-6 relative hover:border-white/30 transition-colors" data-id="element-1587" data-mp="csi5i1lx47uo" data-mpi={i}>
                  <div className="w-10 h-10 rounded-full bg-teal text-white font-bold flex items-center justify-center mb-4 mx-auto md:mx-0 shadow-[0_0_15px_rgba(7,133,183,0.5)]" data-id="element-1588" data-mp="3lkt99f4z1zig">
                    {phase.step}
                  </div>
                  <h3 className="font-poppins font-bold text-lg mb-2 text-center md:text-left" data-id="element-1589" data-mp="2ov2fji4i8gdp">
                    {phase.title}
                  </h3>
                  <p className="text-steel text-sm text-center md:text-left" data-id="element-1590" data-mp="32o1qr7x97ofg">
                    {phase.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1591" data-mp="257ux9988qquc" />

      {/* Social Proof & CTA */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1592" data-mp="ko924tj5cmqg">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1593" data-mp="ph2459n8dgyu" />

        <div className="inner-container text-center relative z-10" data-id="element-1594" data-mp="gmnsdh1wsokh">
          <p className="text-steel mb-8 uppercase tracking-widest text-sm font-semibold" data-id="element-1595" data-mp="14d1llaw8ctgn">
            Trusted by teams at
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 grayscale mb-16" data-id="element-1596" data-mp="18dlicdcw5v29">
            {/* Placeholder logos */}
            <div className="text-xl font-bold font-poppins" data-id="element-1597" data-mp="uh7ywyosrjl5">Acme Corp</div>
            <div className="text-xl font-bold font-poppins" data-id="element-1598" data-mp="zc3sw4dlgh0w">GlobalTech</div>
            <div className="text-xl font-bold font-poppins" data-id="element-1599" data-mp="3f41dvhu3e6mr">
              Nexus Industries
            </div>
            <div className="text-xl font-bold font-poppins" data-id="element-1600" data-mp="3k4yzk4eb9it0">
              Stark Enterprises
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple/20 to-teal/20 backdrop-blur-md border border-white/10 rounded-2xl p-12 max-w-3xl mx-auto shadow-[0_0_40px_rgba(124,58,237,0.15)] relative overflow-hidden" data-id="element-1601" data-mp="3045na5dwj9qr">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full blur-[80px] pointer-events-none" data-id="element-1602" data-mp="2mao62r8ou93w" />
            <h2 className="font-poppins font-bold text-3xl mb-4 relative z-10" data-id="element-1603" data-mp="2hd4pvlsfjm9b">
              Ready to scale your operations?
            </h2>
            <p className="text-steel mb-8 relative z-10" data-id="element-1604" data-mp="28alqxcrrltsc">
              Skip the standard queue. Speak directly with our enterprise
              solutions team.
            </p>
            <button className="btn-primary flex items-center gap-2 mx-auto relative z-10" data-id="element-1605" data-mp="23e53ylwaxlgu">
              Talk to Sales <ArrowRight size={20} data-id="element-1606" data-mp="6h5xk84am0to" />
            </button>
          </div>
        </div>
      </section>
    </main>;
}